/**
 * Renewal condition and countdown (DECISIONS.md A-R3, A-R4, A-R10; ADR 0003).
 *
 * Both are derived on every read from the renewal date and "today" in the business timezone,
 * and are never stored. All arithmetic is on calendar dates (YYYY-MM-DD), never timestamps, so
 * the time of day and daylight-saving rules cannot shift a result.
 */

export const BUSINESS_TIME_ZONE = 'Africa/Lagos';

/** A calendar date as YYYY-MM-DD. */
export type IsoDate = string;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

function parts(date: IsoDate): [number, number, number] {
  const match = ISO_DATE.exec(date);
  if (!match) throw new RangeError(`Not a YYYY-MM-DD date: ${date}`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** True for a real calendar date in YYYY-MM-DD form (rejects e.g. 2026-02-30). */
export function isValidIsoDate(value: string): boolean {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

function dayNumber(date: IsoDate): number {
  const [year, month, day] = parts(date);
  return Date.UTC(year, month - 1, day) / DAY_MS;
}

function format(year: number, month: number, day: number): IsoDate {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Today's date in the given timezone at instant `now`. */
export function todayIn(now: Date, timeZone: string = BUSINESS_TIME_ZONE): IsoDate {
  const byType = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  );
  return format(Number(byType.year), Number(byType.month), Number(byType.day));
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return dayNumber(to) - dayNumber(from);
}

/** Adds calendar months, keeping the day of month but clamping to the target month's last day. */
export function addMonths(date: IsoDate, months: number): IsoDate {
  const [year, month, day] = parts(date);
  const index = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(index / 12);
  const targetMonth = (index % 12) + 1;
  return format(targetYear, targetMonth, Math.min(day, daysInMonth(targetYear, targetMonth)));
}

/** The largest n ≥ 0 such that addMonths(from, n) is on or before `to`. */
export function wholeCalendarMonths(from: IsoDate, to: IsoDate): number {
  if (dayNumber(to) < dayNumber(from)) return 0;
  const [fy, fm] = parts(from);
  const [ty, tm] = parts(to);
  let months = (ty - fy) * 12 + (tm - fm);
  while (months > 0 && dayNumber(addMonths(from, months)) > dayNumber(to)) months -= 1;
  return months;
}

// ---------------------------------------------------------------------------
// Renewal condition (A-R3)
// ---------------------------------------------------------------------------

export const RENEWAL_CONDITIONS = ['upcoming', 'due_soon', 'due_today', 'overdue'] as const;
export type RenewalCondition = (typeof RENEWAL_CONDITIONS)[number];

/** > 30 days upcoming · 1–30 days due_soon · today due_today · past overdue. */
export function renewalCondition(renewalDate: IsoDate, today: IsoDate): RenewalCondition {
  const days = daysBetween(today, renewalDate);
  if (days < 0) return 'overdue';
  if (days === 0) return 'due_today';
  if (days <= 30) return 'due_soon';
  return 'upcoming';
}

// ---------------------------------------------------------------------------
// Countdown (A-R4, A-R10)
// ---------------------------------------------------------------------------

export type CountdownUnit = 'day' | 'week' | 'month';

export type Countdown =
  | { kind: 'due_today'; value: null; unit: null; label: string }
  | { kind: 'remaining'; value: number; unit: CountdownUnit; label: string }
  | { kind: 'overdue'; value: number; unit: 'day'; label: string };

function quantity(value: number, unit: CountdownUnit): string {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

function remaining(value: number, unit: CountdownUnit): Countdown {
  return { kind: 'remaining', value, unit, label: quantity(value, unit) };
}

/**
 * Today → "Due today"; 1–6 days → days; 7–29 days → weeks (rounded down); 30+ days → whole
 * calendar months (rounded down), falling back to weeks when under one whole month so a future
 * date never shows "0 months"; past → "Overdue by N days".
 */
export function renewalCountdown(renewalDate: IsoDate, today: IsoDate): Countdown {
  const days = daysBetween(today, renewalDate);
  if (days === 0) return { kind: 'due_today', value: null, unit: null, label: 'Due today' };
  if (days < 0) {
    const overdue = -days;
    return {
      kind: 'overdue',
      value: overdue,
      unit: 'day',
      label: `Overdue by ${quantity(overdue, 'day')}`,
    };
  }
  if (days <= 6) return remaining(days, 'day');
  if (days <= 29) return remaining(Math.floor(days / 7), 'week');
  const months = wholeCalendarMonths(today, renewalDate);
  return months >= 1 ? remaining(months, 'month') : remaining(Math.floor(days / 7), 'week');
}
